import React, { useState, useEffect, useCallback, useRef } from 'react'
import { FiArrowRight, FiArrowLeft, FiSearch, FiDownload } from 'react-icons/fi'
import axios from '../config/axios'
import { useSearchStore } from '../store/searchStore'
import { useAuthStore } from '../store/authStore'

// ─── Dual range slider ────────────────────────────────────────────────────────

const ZOOM_THRESHOLD = 30

function posToValue(clientX, trackEl, lo, hi, step = 1) {
  const rect = trackEl.getBoundingClientRect()
  const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
  const raw = lo + ratio * (hi - lo)
  return Math.round(raw / step) * step
}

function DragThumb({ trackRef, trMin, trMax, ariaLabel, ariaValueNow, ariaValueMin, ariaValueMax, onChange, style, className }) {
  const dragging = useRef(false)

  const handlePointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
  }, [])

  const handlePointerMove = useCallback((e) => {
    if (!dragging.current || !trackRef.current) return
    e.preventDefault()
    const v = posToValue(e.clientX, trackRef.current, trMin, trMax)
    onChange(v)
  }, [trackRef, trMin, trMax, onChange])

  const handlePointerUp = useCallback((e) => {
    dragging.current = false
    e.currentTarget.releasePointerCapture(e.pointerId)
  }, [])

  const handleKeyDown = useCallback((e) => {
    const step = 1
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault()
      onChange(Math.min(trMax, ariaValueNow + step))
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault()
      onChange(Math.max(trMin, ariaValueNow - step))
    } else if (e.key === 'Home') {
      e.preventDefault()
      onChange(trMin)
    } else if (e.key === 'End') {
      e.preventDefault()
      onChange(trMax)
    }
  }, [trMin, trMax, ariaValueNow, onChange])

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-valuemin={ariaValueMin}
      aria-valuemax={ariaValueMax}
      aria-valuenow={ariaValueNow}
      className={className}
      style={style}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    />
  )
}

function DualRangeSlider({ label, unit, min, max, low, high, onLowChange, onHighChange, onMainLowChange, onMainHighChange, zoomBase }) {
  const trackRef = useRef(null)
  const zoomTrackRef = useRef(null)
  const lowInputRef = useRef(null)
  const highInputRef = useRef(null)

  // Local text state so the box can be cleared / hold partial input while typing
  const [lowText, setLowText] = useState(String(low))
  const [highText, setHighText] = useState(String(high))

  // Keep text in sync when the numeric value changes from sliders/elsewhere (but not while focused)
  useEffect(() => { if (document.activeElement !== lowInputRef.current) setLowText(String(low)) }, [low])
  useEffect(() => { if (document.activeElement !== highInputRef.current) setHighText(String(high)) }, [high])

  const pct = (v, lo, hi) => ((v - lo) / (hi - lo)) * 100

  const commitLow = (str) => {
    if (str === '' || str === '-' || str.endsWith('.')) return
    const v = Math.round(parseFloat(str) * 100) / 100
    if (!isNaN(v)) (onMainLowChange ?? onLowChange)(Math.max(min, Math.min(v, high)))
  }
  const commitHigh = (str) => {
    if (str === '' || str === '-' || str.endsWith('.')) return
    const v = Math.round(parseFloat(str) * 100) / 100
    if (!isNaN(v)) (onMainHighChange ?? onHighChange)(Math.min(max, Math.max(v, low)))
  }

  const showZoom = zoomBase && (high - low) >= 1 && (high - low) <= ZOOM_THRESHOLD
  const zMin = zoomBase?.low ?? min
  const zMax = zoomBase?.high ?? max

  const handleMainLow = useCallback((v) => {
    const clamped = Math.max(min, Math.min(v, high - 1))
    ;(onMainLowChange ?? onLowChange)(clamped)
  }, [min, high, onMainLowChange, onLowChange])

  const handleMainHigh = useCallback((v) => {
    const clamped = Math.min(max, Math.max(v, low + 1))
    ;(onMainHighChange ?? onHighChange)(clamped)
  }, [max, low, onMainHighChange, onHighChange])

  // Zoom thumb handlers — clamp within zoom bounds and keep thumbs apart
  const handleZoomLow = useCallback((v) => {
    onLowChange(Math.max(zMin, Math.min(v, high - 1)))
  }, [zMin, high, onLowChange])

  const handleZoomHigh = useCallback((v) => {
    onHighChange(Math.min(zMax, Math.max(v, low + 1)))
  }, [zMax, low, onHighChange])

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-white">{label} <span className="text-gray-500 font-normal">[{unit}]</span></span>
        <span className="text-xs text-gray-400">{low} – {high} {unit}</span>
      </div>

      {/* Main track */}
      <div className="relative h-8 flex items-center" ref={trackRef}>
        {/* Background track */}
        <div className="absolute w-full h-1 rounded-full bg-gray-700 top-1/2 -translate-y-1/2" />

        {/* Highlight track (from low to high) */}
        <div
          className="absolute h-1 rounded-full bg-accent-teal top-1/2 -translate-y-1/2"
          style={{ left: `${pct(low, min, max)}%`, right: `${100 - pct(high, min, max)}%` }}
        />

        {/* Low visual thumb — draggable, full hit area at any position */}
        <DragThumb
          trackRef={trackRef}
          trMin={min} trMax={max}
          ariaLabel={`${label} minimum`}
          ariaValueNow={low} ariaValueMin={min} ariaValueMax={high - 1}
          onChange={handleMainLow}
          className="slider-visual-thumb"
          style={{ left: `${pct(low, min, max)}%`, zIndex: 6, cursor: 'grab', pointerEvents: 'auto' }}
        />

        {/* High visual thumb — draggable, full hit area at any position */}
        <DragThumb
          trackRef={trackRef}
          trMin={min} trMax={max}
          ariaLabel={`${label} maximum`}
          ariaValueNow={high} ariaValueMin={low + 1} ariaValueMax={max}
          onChange={handleMainHigh}
          className="slider-visual-thumb"
          style={{ left: `${pct(high, min, max)}%`, zIndex: 6, cursor: 'grab', pointerEvents: 'auto' }}
        />
      </div>

      {/* Numeric inputs */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">from</span>
          <input
            ref={lowInputRef}
            type="number"
            step="1"
            min={min}
            max={high}
            value={lowText}
            onChange={e => { setLowText(e.target.value); commitLow(e.target.value) }}
            onBlur={() => setLowText(String(low))}
            className="w-20 px-2 py-1 text-sm bg-bg-dark border border-border-dark rounded text-white text-center focus:outline-none focus:border-accent-teal"
          />
        </div>
        <span className="text-gray-600">—</span>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500">until</span>
          <input
            ref={highInputRef}
            type="number"
            step="1"
            min={low}
            max={max}
            value={highText}
            onChange={e => { setHighText(e.target.value); commitHigh(e.target.value) }}
            onBlur={() => setHighText(String(high))}
            className="w-20 px-2 py-1 text-sm bg-bg-dark border border-border-dark rounded text-white text-center focus:outline-none focus:border-accent-teal"
          />
        </div>
      </div>

      {/* Zoom sub-slider — dual thumb, appears when gap ≤ ZOOM_THRESHOLD */}
      {showZoom && (
        <div className="pt-1 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Zoom</span>
            <span className="text-xs text-gray-500">{low} – {high} {unit}</span>
          </div>
          <div className="relative h-8 flex items-center" ref={zoomTrackRef}>
            {/* Background track */}
            <div className="absolute w-full h-1 rounded-full bg-gray-700 top-1/2 -translate-y-1/2" />

            {/* Highlight between zoom thumbs */}
            <div
              className="absolute h-1 rounded-full bg-accent-yellow/60 top-1/2 -translate-y-1/2"
              style={{ left: `${pct(low, zMin, zMax)}%`, right: `${100 - pct(high, zMin, zMax)}%` }}
            />

            {/* Zoom low thumb */}
            <DragThumb
              trackRef={zoomTrackRef}
              trMin={zMin} trMax={zMax}
              ariaLabel={`${label} zoom minimum`}
              ariaValueNow={low} ariaValueMin={zMin} ariaValueMax={high - 1}
              onChange={handleZoomLow}
              className="slider-visual-thumb"
              style={{ left: `${pct(low, zMin, zMax)}%`, zIndex: 7, cursor: 'grab', pointerEvents: 'auto', background: '#d97706', boxShadow: '0 2px 10px rgba(217,119,6,0.35)' }}
            />

            {/* Zoom high thumb */}
            <DragThumb
              trackRef={zoomTrackRef}
              trMin={zMin} trMax={zMax}
              ariaLabel={`${label} zoom maximum`}
              ariaValueNow={high} ariaValueMin={low + 1} ariaValueMax={zMax}
              onChange={handleZoomHigh}
              className="slider-visual-thumb"
              style={{ left: `${pct(high, zMin, zMax)}%`, zIndex: 7, cursor: 'grab', pointerEvents: 'auto', background: '#d97706', boxShadow: '0 2px 10px rgba(217,119,6,0.35)' }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Series tile ──────────────────────────────────────────────────────────────

function SeriesTile({ item, onClick }) {
  const imgSrc = `/cases-images/${item.sampleCatalog?.replace(/\//g, '_')}.png`
  const [imgError, setImgError] = useState(false)

  return (
    <button
      onClick={onClick}
      className="group relative bg-bg-card border border-border-dark rounded-xl overflow-hidden text-left transition hover:border-accent-teal/60 hover:shadow-lg hover:shadow-accent-teal/5 active:scale-[0.98]"
    >
      {/* Image */}
      <div className="h-36 bg-bg-card flex items-center justify-center p-4">
        {!imgError ? (
          <img
            src={imgSrc}
            alt={item.series}
            onError={() => setImgError(true)}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="text-gray-700 text-4xl select-none">□</div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 space-y-2">
        <h3 className="text-accent-teal font-bold text-base leading-tight group-hover:text-cyan-300 transition">
          {item.series}
        </h3>
        <p className="text-gray-400 text-xs">{item.manufacturer}</p>

        {/* Variant counts */}
        <p className="text-gray-500 text-xs">{item.count} {item.count === 1 ? 'Variant' : 'Variants'} in requested Dims</p>

        {item.altCount !== undefined && item.altCount > 0 && (
          <>
            <div className="h-px bg-accent-yellow my-1" />
            <p className="text-gray-500 text-xs">
              +{item.altCount} Variants Different Orientation
            </p>
          </>
        )}
      </div>

      {/* Arrow */}
      <div className="absolute bottom-4 right-4 w-7 h-7 rounded-full bg-accent-teal/10 border border-accent-teal/30 flex items-center justify-center group-hover:bg-accent-teal group-hover:border-accent-teal transition">
        <FiArrowRight size={13} className="text-accent-teal group-hover:text-bg-dark transition" />
      </div>
    </button>
  )
}

// ─── Product card ─────────────────────────────────────────────────────────────

function ProductCard({ c }) {
  const imgSrc = `/cases-images/${c.catalog?.replace(/\//g, '_')}.png`
  const [imgError, setImgError] = useState(false)
  const fmt = v => parseFloat(parseFloat(v).toFixed(1))

  return (
    <div className="bg-bg-card border border-border-dark rounded-xl overflow-hidden flex flex-col">
      {/* Image */}
      <div className="h-40 bg-bg-card flex items-center justify-center p-4">
        {!imgError ? (
          <img src={imgSrc} alt={c.catalog} onError={() => setImgError(true)} className="h-full w-full object-contain" />
        ) : (
          <div className="text-gray-700 text-5xl select-none">□</div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div>
          <p className="text-accent-teal font-bold text-sm">{c.series}</p>
          <p className="text-gray-400 text-xs mt-0.5">Mnf#: <span className="text-accent-yellow font-semibold">{c.catalog}</span></p>
        </div>

        {/* Orientation indicator for alternatives */}
        {c.isAlternative && (
          <p className="text-xs text-accent-yellow bg-accent-yellow/10 px-2 py-1 rounded">
            {c.orientationName}
          </p>
        )}

        <div className="text-xs text-gray-500 space-y-2 mt-1">
          <div>
            <p className="text-gray-400 font-semibold">Internal Dimensions</p>
            <p className="text-gray-500">L × W × H (B + L)</p>
            <p className="text-white">
              {fmt(c.intLengthCm)} × {fmt(c.intWidthCm)} × {fmt(c.intDepthCm)} cm
            </p>
            {(c.lidDepth > 0 || c.baseDepth > 0) && (
              <p className="text-gray-400 text-[11px]">
                ({fmt(c.baseDepth)} base + {fmt(c.lidDepth)} lid)
              </p>
            )}
          </div>

          {(c.extLengthCm > 0 || c.extWidthCm > 0 || c.extDepthCm > 0) && (
            <div>
              <p className="text-gray-400 font-semibold">External Dimensions</p>
              <p className="text-gray-500">L × W × H</p>
              <p className="text-white">
                {fmt(c.extLengthCm)} × {fmt(c.extWidthCm)} × {fmt(c.extDepthCm)} cm
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main configurator ────────────────────────────────────────────────────────

export default function ConfiguratorPage({ onSwitchToClassic, savedState = {}, onStateChange = () => {} }) {
  const { search, setSelectedSeries, fetchSeries, processes, setSelectedProcess } = useSearchStore()
  const { user } = useAuthStore()

  const [ranges, setRanges] = useState(null)
  const [seriesList, setSeriesList] = useState([])
  const [countLoading, setCountLoading] = useState(false)
  const [totalCount, setTotalCount] = useState(0)

  // Slider state — use saved state if available, otherwise start at min/max range
  const [lengthLow, setLengthLow] = useState(savedState.lengthLow !== null && savedState.lengthLow !== undefined ? savedState.lengthLow : null)
  const [lengthHigh, setLengthHigh] = useState(savedState.lengthHigh !== null && savedState.lengthHigh !== undefined ? savedState.lengthHigh : null)
  const [widthLow, setWidthLow] = useState(savedState.widthLow !== null && savedState.widthLow !== undefined ? savedState.widthLow : null)
  const [widthHigh, setWidthHigh] = useState(savedState.widthHigh !== null && savedState.widthHigh !== undefined ? savedState.widthHigh : null)
  const [depthLow, setDepthLow] = useState(savedState.depthLow !== null && savedState.depthLow !== undefined ? savedState.depthLow : null)
  const [depthHigh, setDepthHigh] = useState(savedState.depthHigh !== null && savedState.depthHigh !== undefined ? savedState.depthHigh : null)

  // Zoom base anchors — the outer extents the zoom track spans; reset when main thumbs move
  const [lengthBase, setLengthBase] = useState({ low: null, high: null })
  const [widthBase,  setWidthBase]  = useState({ low: null, high: null })
  const [depthBase,  setDepthBase]  = useState({ low: null, high: null })

  // Capture savedState at mount only — prevents effect re-runs when parent re-renders
  const savedStateRef = useRef(savedState)

  // Drill-down state
  const [selectedSerie, setSelectedSerie] = useState(null) // { series, manufacturer }
  const [serieCases, setSerieCases] = useState([])
  const [caseLoading, setCaseLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Load dimension ranges on mount — runs once only (savedState read via ref)
  useEffect(() => {
    const ss = savedStateRef.current
    axios.get('/api/search/dimension-ranges').then(r => {
      const { length, width, depth } = r.data.ranges
      setRanges(r.data.ranges)
      if (ss.lengthLow === null || ss.lengthLow === undefined) {
        setLengthLow(0); setLengthHigh(length.max)
        setWidthLow(0);  setWidthHigh(width.max)
        setDepthLow(0);  setDepthHigh(depth.max)
        setLengthBase({ low: 0, high: length.max })
        setWidthBase({ low: 0, high: width.max })
        setDepthBase({ low: 0, high: depth.max })
      } else {
        setLengthBase({ low: ss.lengthLow, high: ss.lengthHigh })
        setWidthBase({ low: ss.widthLow,   high: ss.widthHigh })
        setDepthBase({ low: ss.depthLow,   high: ss.depthHigh })
      }
    }).catch(() => {})
    fetchSeries()
  }, [fetchSeries]) // eslint-disable-line react-hooks/exhaustive-deps

  // Save state whenever sliders change
  useEffect(() => {
    onStateChange({ lengthLow, lengthHigh, widthLow, widthHigh, depthLow, depthHigh })
  }, [lengthLow, lengthHigh, widthLow, widthHigh, depthLow, depthHigh, onStateChange])

  // Check if using default max ranges (user hasn't customized yet)
  const isDefaultRange = () => {
    if (!ranges) return false
    return lengthLow === 0 && lengthHigh === ranges.length.max &&
           widthLow === 0 && widthHigh === ranges.width.max &&
           depthLow === 0 && depthHigh === ranges.depth.max
  }

  // Debounced count-by-series whenever sliders change
  const debounceRef = useRef(null)
  const fetchCounts = useCallback(() => {
    if (!ranges) return
    clearTimeout(debounceRef.current)

    // Don't fetch if using default ranges
    if (isDefaultRange()) {
      setSeriesList([])
      setTotalCount(0)
      setCountLoading(false)
      return
    }

    debounceRef.current = setTimeout(async () => {
      setCountLoading(true)
      try {
        const r = await axios.post('/api/search/count-by-series', {
          minLength: lengthLow, maxLength: lengthHigh,
          minWidth: widthLow,   maxWidth: widthHigh,
          minDepth: depthLow,   maxDepth: depthHigh,
          skipVolumeFilter: true,
        })
        // Filter to allowed manufacturers
        const allowed = user?.allowedManufacturers || []
        const isAdmin = user?.role === 'admin'
        const filtered = r.data.results.filter(s =>
          isAdmin || allowed.includes('*') || allowed.length === 0 || allowed.includes(s.manufacturer)
        )
        setSeriesList(filtered)
        setTotalCount(filtered.reduce((sum, s) => sum + s.count + (s.altCount || 0), 0))
      } catch {}
      setCountLoading(false)
    }, 300)
  }, [ranges, lengthLow, lengthHigh, widthLow, widthHigh, depthLow, depthHigh, user])

  useEffect(() => { fetchCounts() }, [fetchCounts])

  // Load cases for a series tile click
  const handleTileClick = async (item) => {
    setSelectedSerie(item)
    setCaseLoading(true)
    try {
      const r = await axios.post('/api/search/series-cases', {
        series: item.series,
        manufacturer: item.manufacturer,
        minLength: lengthLow, maxLength: lengthHigh,
        minWidth: widthLow,   maxWidth: widthHigh,
        minDepth: depthLow,   maxDepth: depthHigh,
        skipVolumeFilter: true,
      })
      const allCases = r.data.cases || []

      // 6 permutations for filtering cases by orientation (no volume filter — user set explicit ranges)
      const permutations = [
        { l: lengthLow, h: lengthHigh, w: widthLow, x: widthHigh, d: depthLow, z: depthHigh }, // Original
        { l: lengthLow, h: lengthHigh, w: depthLow, x: depthHigh, d: widthLow, z: widthHigh }, // Swap W↔D
        { l: widthLow, h: widthHigh, w: lengthLow, x: lengthHigh, d: depthLow, z: depthHigh }, // Swap L↔W
        { l: widthLow, h: widthHigh, w: depthLow, x: depthHigh, d: lengthLow, z: lengthHigh }, // L→W, W→D, D→L
        { l: depthLow, h: depthHigh, w: lengthLow, x: lengthHigh, d: widthLow, z: widthHigh }, // L→D, D→W, W→L
        { l: depthLow, h: depthHigh, w: widthLow, x: widthHigh, d: lengthLow, z: lengthHigh }  // All reversed
      ]

      // Categorize each case by which permutation it matches
      const original = []
      const alternatives = []

      allCases.forEach(c => {
        let matchedOriginal = false
        let matchedAlternative = false

        for (let i = 0; i < permutations.length; i++) {
          const perm = permutations[i]
          if (c.intLengthCm >= perm.l && c.intLengthCm <= perm.h &&
              c.intWidthCm >= perm.w && c.intWidthCm <= perm.x &&
              c.intDepthCm >= perm.d && c.intDepthCm <= perm.z) {

            {
              if (i === 0) {
                matchedOriginal = true
              } else {
                matchedAlternative = true
              }
            }
          }
        }

        if (matchedOriginal) {
          original.push(c)
        } else if (matchedAlternative) {
          alternatives.push(c)
        }
      })

      setSerieCases([...original, ...alternatives])
    } catch {}
    setCaseLoading(false)
  }


  if (!ranges || lengthLow === null || lengthHigh === null || widthLow === null || widthHigh === null || depthLow === null || depthHigh === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-teal" />
      </div>
    )
  }

  // Sanitise Unicode arrows that jsPDF Helvetica cannot render
  const pdfSafe = str => str
    .replace(/↔/g, '<->').replace(/→/g, '->').replace(/←/g, '<-')
    .replace(/×/g, 'x')

  // ── Main-page PDF export — all series ────────────────────────────────────
  const handleExportAllPDF = async () => {
    setExporting(true)
    try {
      const { jsPDF } = await import('jspdf')
      const { autoTable } = await import('jspdf-autotable')

      const today = new Date().toISOString().split('T')[0]
      const fmt = v => String(parseFloat(parseFloat(v).toFixed(1)))
      const PAGE_W = 297, PAGE_H = 210, MARGIN = 14, USABLE = PAGE_W - MARGIN * 2

      const toBase64 = (url) => new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth; canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/jpeg', 0.92))
          } catch { resolve(null) }
        }
        img.onerror = () => resolve(null)
        img.src = url
      })

      // Fetch all cases for every visible series in parallel
      const visibleSeries = seriesList.filter(s => s.count > 0 || s.altCount > 0)
      const seriesCasesMap = {}
      await Promise.all(visibleSeries.map(async s => {
        try {
          const r = await axios.post('/api/search/series-cases', {
            series: s.series, manufacturer: s.manufacturer,
            minLength: lengthLow, maxLength: lengthHigh,
            minWidth: widthLow,   maxWidth: widthHigh,
            minDepth: depthLow,   maxDepth: depthHigh,
            skipVolumeFilter: true,
          })
          seriesCasesMap[`${s.manufacturer}__${s.series}`] = r.data.cases || []
        } catch { seriesCasesMap[`${s.manufacturer}__${s.series}`] = [] }
      }))

      // Collect all unique catalogs for image pre-fetch
      const allCases = Object.values(seriesCasesMap).flat()
      const imageMap = {}
      await Promise.all([...new Set(allCases.map(c => c.catalog))].map(async catalog => {
        imageMap[catalog] = await toBase64(`/cases-images/${catalog.replace(/\//g, '_')}.png`)
      }))

      // Pre-fetch logos
      const allMfrs = [...new Set(allCases.map(c => c.manufacturer))]
      const logoMap = {}
      await Promise.all(allMfrs.map(async mfr => {
        const file = mfr === 'Pelican Products' ? '/logo-pelican.png'
                   : mfr === 'GT Line Explorer' ? '/logo-explorer.png'
                   : mfr === 'Suprobox'         ? '/logo-suprobox 1.png'
                   : null
        if (file) logoMap[mfr] = await toBase64(file)
      }))

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
      const IMG_W = 38, IMG_H = 32, IMG_GAP = 4, IMG_LABEL_H = 12

      // ── Cover header ──
      doc.setFontSize(13); doc.setFont('helvetica', 'bold'); doc.setTextColor(40)
      doc.text('Case Sizing Engine - Configurator Results', MARGIN, 13)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(120)
      doc.text(`Generated: ${today}`, MARGIN, 19)
      doc.text(
        `Requested Range: L ${fmt(lengthLow)}–${fmt(lengthHigh)} × W ${fmt(widthLow)}–${fmt(widthHigh)} × D ${fmt(depthLow)}–${fmt(depthHigh)} cm  |  ${totalCount} cases across ${visibleSeries.length} series`,
        MARGIN, 24
      )

      // Logos top-right of cover
      let logoX = PAGE_W - MARGIN
      allMfrs.forEach(mfr => {
        const logo = logoMap[mfr]; if (!logo) return
        const w = mfr === 'GT Line Explorer' ? 30 : 28
        logoX -= w
        try { doc.addImage(logo, 'PNG', logoX, 6, w, 10) } catch {}
        logoX -= 4
      })

      let startY = 30
      let isFirstSeries = true

      for (const s of visibleSeries) {
        const key = `${s.manufacturer}__${s.series}`
        const cases = seriesCasesMap[key] || []
        if (cases.length === 0) continue

        const originalCases = cases.filter(c => !c.isAlternative)
        const altCases      = cases.filter(c =>  c.isAlternative)

        // Render one group (original or alt) for this series
        const renderGroup = (groupCases, groupLabel, isAlt) => {
          if (groupCases.length === 0) return

          const estTableH = 14 + groupCases.length * 7
          const estBlockH = estTableH + IMG_H + IMG_LABEL_H + 8

          if (!isFirstSeries && isAlt === false && startY + estBlockH > PAGE_H - 16) {
            doc.addPage(); startY = 14
          } else if (startY + estBlockH > PAGE_H - 16) {
            doc.addPage(); startY = 14
          }

          // Series name header on first group of this series
          if (!isAlt) {
            doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(0, 180, 180)
            doc.text(`${s.series}`, MARGIN, startY)
            doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(120)
            doc.text(`${s.manufacturer}`, MARGIN + doc.getTextWidth(`${s.series}`) + 4, startY)

            // Manufacturer logo inline
            const logo = logoMap[s.manufacturer]
            if (logo) { try { doc.addImage(logo, 'PNG', PAGE_W - MARGIN - 28, startY - 7, 28, 9) } catch {} }
            startY += 5
          }

          autoTable(doc, {
            startY,
            head: [
              [{ content: groupLabel, colSpan: 10, styles: { fillColor: isAlt ? [41, 37, 10] : [30, 41, 59], textColor: isAlt ? [251, 191, 36] : [148, 226, 213], fontStyle: 'bold', fontSize: 7.5 } }],
              ['Model', 'Series', 'Manufacturer', 'Int L', 'Int W', 'Int D', 'Base', 'Lid', 'Ext L', 'Ext W'].map(h => ({ content: h, styles: { fontStyle: 'bold' } }))
            ],
            body: groupCases.map(c => [
              c.catalog, c.series, c.manufacturer,
              fmt(c.intLengthCm), fmt(c.intWidthCm), fmt(c.intDepthCm),
              fmt(c.baseDepth), fmt(c.lidDepth),
              c.extLengthCm > 0 ? fmt(c.extLengthCm) : '—',
              c.extWidthCm  > 0 ? fmt(c.extWidthCm)  : '—',
            ]),
            headStyles:         { fillColor: [15, 23, 42], textColor: [203, 213, 225], fontSize: 6.5, fontStyle: 'bold' },
            bodyStyles:         { fontSize: 6.5, textColor: [50, 50, 50], fillColor: [245, 245, 245] },
            alternateRowStyles: { fillColor: [230, 235, 240] },
            columnStyles: {
              0: { cellWidth: 28 }, 1: { cellWidth: 22 }, 2: { cellWidth: 26 },
              3: { cellWidth: 19 }, 4: { cellWidth: 19 }, 5: { cellWidth: 19 },
              6: { cellWidth: 16 }, 7: { cellWidth: 16 },
              8: { cellWidth: 19 }, 9: { cellWidth: 19 },
            },
            margin: { left: MARGIN, right: MARGIN },
            theme: 'grid',
          })

          // Images row
          let imgY = doc.lastAutoTable.finalY + 4
          if (imgY + IMG_H + IMG_LABEL_H > PAGE_H - 14) { doc.addPage(); imgY = 14 }

          groupCases.slice(0, 6).forEach((c, idx) => {
            const imgX = MARGIN + idx * (IMG_W + IMG_GAP)
            const b64 = imageMap[c.catalog]
            if (b64) { try { doc.addImage(b64, 'JPEG', imgX, imgY, IMG_W, IMG_H) } catch {} }
            else { doc.setDrawColor(180); doc.rect(imgX, imgY, IMG_W, IMG_H) }
            doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(40)
            doc.text(String(c.catalog), imgX + IMG_W / 2, imgY + IMG_H + 4, { align: 'center', maxWidth: IMG_W })
            doc.setFont('helvetica', 'normal'); doc.setTextColor(100)
            doc.text(`L${fmt(c.intLengthCm)} x W${fmt(c.intWidthCm)} x D${fmt(c.intDepthCm)}`, imgX + IMG_W / 2, imgY + IMG_H + 8, { align: 'center', maxWidth: IMG_W })
            if (isAlt && c.orientationName) {
              doc.setTextColor(160, 120, 0)
              doc.text(pdfSafe(c.orientationName), imgX + IMG_W / 2, imgY + IMG_H + 12, { align: 'center', maxWidth: IMG_W })
            }
          })

          startY = imgY + IMG_H + IMG_LABEL_H + 6
        }

        renderGroup(originalCases, `In Requested Range  |  All dimensions are internal (cm)`, false)
        renderGroup(altCases,      `Alternative Orientations — item fits when case is rotated  |  All dimensions are internal (cm)`, true)
        isFirstSeries = false
      }

      // Footer on last page
      doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(130)
      doc.text(
        doc.splitTextToSize('Suggestions only; final decision is the sole responsibility of the user.', USABLE),
        MARGIN, Math.min(startY + 2, PAGE_H - 8)
      )

      doc.save(`Case Sizing Engine Configurator ${today}.pdf`)
    } catch (err) {
      console.error('Configurator all-series PDF error:', err)
      if (err?.message?.includes('Failed to fetch dynamically imported module') || err?.message?.includes('Importing a module script failed')) {
        if (confirm('The app has been updated. Reload the page to apply the update, then try again.')) {
          window.location.reload()
        }
      } else {
        alert(`PDF export failed: ${err.message}`)
      }
    } finally {
      setExporting(false)
    }
  }

  // ── Configurator PDF export ───────────────────────────────────────────────
  const handleExportPDF = async () => {
    setExporting(true)
    try {
      const { jsPDF } = await import('jspdf')
      const { autoTable } = await import('jspdf-autotable')

      const today = new Date().toISOString().split('T')[0]
      const fmt = v => String(parseFloat(parseFloat(v).toFixed(1)))
      const PAGE_W = 297, PAGE_H = 210, MARGIN = 14, USABLE = PAGE_W - MARGIN * 2

      const toBase64 = (url) => new Promise(resolve => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas')
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            ctx.fillStyle = '#ffffff'
            ctx.fillRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            resolve(canvas.toDataURL('image/jpeg', 0.92))
          } catch { resolve(null) }
        }
        img.onerror = () => resolve(null)
        img.src = url
      })

      // Pre-fetch all product images
      const imageMap = {}
      await Promise.all(serieCases.map(async c => {
        imageMap[c.catalog] = await toBase64(`/cases-images/${c.catalog.replace(/\//g, '_')}.png`)
      }))

      // Pre-fetch manufacturer logos
      const mfrs = [...new Set(serieCases.map(c => c.manufacturer))]
      const logoMap = {}
      await Promise.all(mfrs.map(async mfr => {
        const file = mfr === 'Pelican Products' ? '/logo-pelican.png'
                   : mfr === 'GT Line Explorer' ? '/logo-explorer.png'
                   : mfr === 'Suprobox'         ? '/logo-suprobox 1.png'
                   : null
        if (file) logoMap[mfr] = await toBase64(file)
      }))

      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })

      // ── Header ──
      doc.setFontSize(13)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(40, 40, 40)
      doc.text('Case Sizing Engine - Configurator Results', MARGIN, 13)

      doc.setFontSize(8)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(120)
      doc.text(`Generated: ${today}`, MARGIN, 19)
      doc.text(
        `Series: ${selectedSerie.series}  |  ${selectedSerie.manufacturer}  |  Requested Range: L ${fmt(lengthLow)}–${fmt(lengthHigh)} × W ${fmt(widthLow)}–${fmt(widthHigh)} × D ${fmt(depthLow)}–${fmt(depthHigh)} cm`,
        MARGIN, 24
      )

      // Logos top-right
      let logoX = PAGE_W - MARGIN
      mfrs.forEach(mfr => {
        const logo = logoMap[mfr]
        if (!logo) return
        const w = mfr === 'GT Line Explorer' ? 30 : 28
        logoX -= w
        try { doc.addImage(logo, 'PNG', logoX, 6, w, 10) } catch {}
        logoX -= 4
      })

      // Split cases into original-orientation and alternative-orientation groups
      const originalCases = serieCases.filter(c => !c.isAlternative)
      const altCases      = serieCases.filter(c =>  c.isAlternative)

      const IMG_W = 38, IMG_H = 32, IMG_GAP = 4, IMG_LABEL_H = 12
      let startY = 30

      const renderGroup = (cases, title) => {
        if (cases.length === 0) return

        const estTableH = 14 + cases.length * 7
        const estBlockH = estTableH + IMG_H + IMG_LABEL_H + 8
        if (startY + estBlockH > PAGE_H - 16) { doc.addPage(); startY = 14 }

        autoTable(doc, {
          startY,
          head: [
            [{ content: title, colSpan: 10, styles: { fillColor: [30, 41, 59], textColor: [148, 226, 213], fontStyle: 'bold', fontSize: 8 } }],
            [
              { content: 'Model',        styles: { fontStyle: 'bold' } },
              { content: 'Series',       styles: { fontStyle: 'bold' } },
              { content: 'Manufacturer', styles: { fontStyle: 'bold' } },
              { content: 'Int L (cm)',   styles: { fontStyle: 'bold' } },
              { content: 'Int W (cm)',   styles: { fontStyle: 'bold' } },
              { content: 'Int D (cm)',   styles: { fontStyle: 'bold' } },
              { content: 'Base (cm)',    styles: { fontStyle: 'bold' } },
              { content: 'Lid (cm)',     styles: { fontStyle: 'bold' } },
              { content: 'Ext L (cm)',   styles: { fontStyle: 'bold' } },
              { content: 'Ext W (cm)',   styles: { fontStyle: 'bold' } },
            ]
          ],
          body: cases.map(c => [
            String(c.catalog),
            String(c.series),
            String(c.manufacturer),
            fmt(c.intLengthCm), fmt(c.intWidthCm), fmt(c.intDepthCm),
            fmt(c.baseDepth),   fmt(c.lidDepth),
            c.extLengthCm > 0 ? fmt(c.extLengthCm) : '—',
            c.extWidthCm  > 0 ? fmt(c.extWidthCm)  : '—',
          ]),
          headStyles:          { fillColor: [15, 23, 42], textColor: [203, 213, 225], fontSize: 7, fontStyle: 'bold' },
          bodyStyles:          { fontSize: 7, textColor: [50, 50, 50], fillColor: [245, 245, 245] },
          alternateRowStyles:  { fillColor: [230, 235, 240] },
          columnStyles: {
            0: { cellWidth: 28 }, 1: { cellWidth: 24 }, 2: { cellWidth: 28 },
            3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 20 },
            6: { cellWidth: 18 }, 7: { cellWidth: 18 },
            8: { cellWidth: 20 }, 9: { cellWidth: 20 },
          },
          margin: { left: MARGIN, right: MARGIN },
          theme: 'grid',
        })

        // Images row
        let imgY = doc.lastAutoTable.finalY + 4
        if (imgY + IMG_H + IMG_LABEL_H > PAGE_H - 14) { doc.addPage(); imgY = 14 }

        cases.slice(0, 6).forEach((c, idx) => {
          const imgX = MARGIN + idx * (IMG_W + IMG_GAP)
          const b64 = imageMap[c.catalog]
          if (b64) {
            try { doc.addImage(b64, 'JPEG', imgX, imgY, IMG_W, IMG_H) } catch {}
          } else {
            doc.setDrawColor(180); doc.rect(imgX, imgY, IMG_W, IMG_H)
          }
          doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(40)
          doc.text(String(c.catalog), imgX + IMG_W / 2, imgY + IMG_H + 4, { align: 'center', maxWidth: IMG_W })
          doc.setFont('helvetica', 'normal'); doc.setTextColor(100)
          doc.text(
            `L ${fmt(c.intLengthCm)} x W ${fmt(c.intWidthCm)} x D ${fmt(c.intDepthCm)}`,
            imgX + IMG_W / 2, imgY + IMG_H + 8, { align: 'center', maxWidth: IMG_W }
          )
          if (c.isAlternative && c.orientationName) {
            doc.setTextColor(160, 120, 0)
            doc.text(pdfSafe(c.orientationName), imgX + IMG_W / 2, imgY + IMG_H + 12, { align: 'center', maxWidth: IMG_W })
          }
        })

        startY = imgY + IMG_H + IMG_LABEL_H + 6
      }

      renderGroup(originalCases, `In Requested Range  |  L ${fmt(lengthLow)}–${fmt(lengthHigh)} × W ${fmt(widthLow)}–${fmt(widthHigh)} × D ${fmt(depthLow)}–${fmt(depthHigh)} cm  |  All dimensions are internal`)
      renderGroup(altCases,      `Alternative Orientations  |  Item fits when case is rotated  |  All dimensions are internal`)

      // Footer
      const noteY = Math.min(startY + 2, PAGE_H - 10)
      if (noteY < PAGE_H - 6) {
        doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(130)
        doc.text(
          doc.splitTextToSize('Suggestions only; final decision is the sole responsibility of the user.', USABLE),
          MARGIN, noteY
        )
      }

      doc.save(`Case Sizing Engine Configurator ${today}.pdf`)
    } catch (err) {
      console.error('Configurator PDF export error:', err)
      if (err?.message?.includes('Failed to fetch dynamically imported module') || err?.message?.includes('Importing a module script failed')) {
        if (confirm('The app has been updated. Reload the page to apply the update, then try again.')) {
          window.location.reload()
        }
      } else {
        alert(`PDF export failed: ${err.message}`)
      }
    } finally {
      setExporting(false)
    }
  }

  // ── Product grid view ──────────────────────────────────────────────────────
  if (selectedSerie) {
    const fmt = v => parseFloat(parseFloat(v).toFixed(1));
    return (
      <div className="space-y-6">
        {/* Back button + header */}
        <div className="flex items-center gap-4 flex-wrap">
          <button
            onClick={() => { setSelectedSerie(null); setSerieCases([]) }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-border-dark text-gray-400 hover:text-white hover:border-gray-400 text-sm transition"
          >
            <FiArrowLeft size={14} /> Back
          </button>
          {serieCases.length > 0 && (
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-accent-teal/50 text-accent-teal text-sm hover:bg-accent-teal/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <FiDownload size={14} /> {exporting ? 'Generating…' : 'Export PDF'}
            </button>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-bold text-accent-teal">{selectedSerie.series}</h2>
              <p className="text-gray-500 text-xs">{selectedSerie.manufacturer} · {selectedSerie.count + (selectedSerie.altCount || 0)} variants in selected range</p>
            </div>
            {/* Dimension range badge */}
            <div className="border border-accent-yellow/60 rounded px-3 py-1.5 bg-accent-yellow/10">
              <p className="text-[10px] text-gray-400 leading-none mb-0.5">Requested Range</p>
              <p className="text-xs font-semibold text-accent-yellow whitespace-nowrap">
                L {fmt(lengthLow)}–{fmt(lengthHigh)} × W {fmt(widthLow)}–{fmt(widthHigh)} × D {fmt(depthLow)}–{fmt(depthHigh)} cm
              </p>
            </div>
          </div>
        </div>

        {caseLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-teal" />
          </div>
        ) : serieCases.length === 0 ? (
          <div className="card text-center py-10 text-gray-400">Welcome to the slider configurator</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {serieCases.map(c => (
              <ProductCard key={c.catalog} c={c} />
            ))}
          </div>
        )}
      </div>
    )
  }

  // ── Main configurator view ─────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-8">

      {/* Left panel — sliders */}
      <div className="lg:col-span-1">
        <div className="card space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold text-white">Inside Dimension</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">
                {countLoading ? '…' : `${totalCount} cases`}
              </span>
              <button
                onClick={() => {
                  setLengthLow(0); setLengthHigh(ranges.length.max)
                  setWidthLow(0);  setWidthHigh(ranges.width.max)
                  setDepthLow(0);  setDepthHigh(ranges.depth.max)
                  setLengthBase({ low: 0, high: ranges.length.max })
                  setWidthBase({ low: 0, high: ranges.width.max })
                  setDepthBase({ low: 0, high: ranges.depth.max })
                }}
                className="text-xs text-gray-500 border border-border-dark rounded px-2 py-0.5 hover:text-white hover:border-gray-400 transition"
              >
                Clear
              </button>
            </div>
          </div>

          <DualRangeSlider
            label="Length" unit="cm"
            min={0} max={ranges.length.max}
            low={lengthLow} high={lengthHigh}
            onLowChange={setLengthLow} onHighChange={setLengthHigh}
            onMainLowChange={v => { setLengthBase({ low: v, high: lengthHigh }); setLengthLow(v) }}
            onMainHighChange={v => { setLengthBase({ low: lengthLow, high: v }); setLengthHigh(v) }}
            zoomBase={lengthBase.low !== null ? lengthBase : null}
          />
          <DualRangeSlider
            label="Width" unit="cm"
            min={0} max={ranges.width.max}
            low={widthLow} high={widthHigh}
            onLowChange={setWidthLow} onHighChange={setWidthHigh}
            onMainLowChange={v => { setWidthBase({ low: v, high: widthHigh }); setWidthLow(v) }}
            onMainHighChange={v => { setWidthBase({ low: widthLow, high: v }); setWidthHigh(v) }}
            zoomBase={widthBase.low !== null ? widthBase : null}
          />
          <DualRangeSlider
            label="Depth" unit="cm"
            min={0} max={ranges.depth.max}
            low={depthLow} high={depthHigh}
            onLowChange={setDepthLow} onHighChange={setDepthHigh}
            onMainLowChange={v => { setDepthBase({ low: v, high: depthHigh }); setDepthLow(v) }}
            onMainHighChange={v => { setDepthBase({ low: depthLow, high: v }); setDepthHigh(v) }}
            zoomBase={depthBase.low !== null ? depthBase : null}
          />

          <div className="pt-2 border-t border-border-dark">
            <p className="text-xs text-gray-500">
              {countLoading
                ? 'Updating…'
                : `${totalCount} cases across ${seriesList.filter(s => s.count > 0 || s.altCount > 0).length} series match`
              }
            </p>
          </div>
        </div>
      </div>

      {/* Right panel — series tiles */}
      <div className="lg:col-span-2 2xl:col-span-3">
        {seriesList.filter(s => s.count > 0 || s.altCount > 0).length > 0 && (
          <div className="flex justify-end mb-4">
            <button
              onClick={handleExportAllPDF}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-accent-teal/50 text-accent-teal text-sm hover:bg-accent-teal/10 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              <FiDownload size={14} /> {exporting ? 'Generating…' : 'Export PDF'}
            </button>
          </div>
        )}
        {countLoading && seriesList.length === 0 ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-teal" />
          </div>
        ) : seriesList.length === 0 ? (
          <div className="card text-center py-12 text-gray-400 space-y-2">
            <p className="text-lg">Welcome to the slider configurator</p>
            <p className="text-sm">Try the sliders for case options</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
            {seriesList.filter(item => item.count > 0 || item.altCount > 0).map(item => (
              <SeriesTile
                key={`${item.manufacturer}__${item.series}`}
                item={item}
                onClick={() => handleTileClick(item)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
